import { useMemo } from "react";

import { useNetworkMetadata, usePermissions } from "Flocknote/apiHooks";

import { useCampaigns } from "Giving/API/queryHooks";
import { rollUpCampaignWithSubCampaigns } from "Giving/campaignRollup";

const parseLocalDate = (dateStr) => new Date(`${dateStr}T00:00:00`);

/**
 * Ongoing, top-level campaigns for the nav, with sub-campaign amounts rolled up
 * into their parent (the campaignsList rows are per-campaign; see
 * Giving/campaignRollup.js). Sub-campaigns are excluded as rows of their own,
 * but a parent-network (diocese) campaign surfaced at the parish counts as
 * top-level. A campaign is ongoing when it has started and either has no end date
 * (open-ended) or its end date is still ahead. Ending a campaign by hand stamps
 * today as its end date (see Network/SettingsPanels/GivingCampaignSettingsPanel/
 * EndCampaignButton.js), so reaching the end date means ended — not ending.
 */
export const getOngoingTopLevelCampaigns = (campaigns, today = new Date()) => {
  const startOfToday = new Date(today);
  startOfToday.setHours(0, 0, 0, 0);

  return campaigns
    .filter((campaign) => {
      const isTopLevel =
        !campaign.parentGivingDesignationCampaignsID ||
        campaign.isParentNetworkCampaign;
      if (!isTopLevel || !campaign.startDate) return false;

      if (parseLocalDate(campaign.startDate) > startOfToday) return false;
      if (campaign.endDate == null) return true;
      return parseLocalDate(campaign.endDate) > startOfToday;
    })
    .map((campaign) =>
      rollUpCampaignWithSubCampaigns(
        campaign,
        campaigns.filter(
          (sub) => sub.parentGivingDesignationCampaignsID === campaign.ID
        )
      )
    );
};

const useOngoingCampaigns = () => {
  const [{ hasFNC }] = useNetworkMetadata();
  const [{ canSeeCampaignsNav }] = usePermissions();

  const enabled = Boolean(hasFNC && canSeeCampaignsNav);

  const { campaigns } = useCampaigns({ enabled });

  const ongoingCampaigns = useMemo(
    () => getOngoingTopLevelCampaigns(campaigns),
    [campaigns]
  );

  const showOngoingCampaigns = enabled && ongoingCampaigns.length > 0;

  return { ongoingCampaigns, enabled, showOngoingCampaigns };
};

export { useOngoingCampaigns };
